import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as aiReviewApi from '@/apis/aiReview';

export const QUERY_KEYS = {
    all: ['ai-review'],
    lists: () => [...QUERY_KEYS.all, 'list'],
    list: (params) => [...QUERY_KEYS.lists(), params],
    details: () => [...QUERY_KEYS.all, 'detail'],
    detail: (id) => [...QUERY_KEYS.details(), id],
    prompts: (id) => [...QUERY_KEYS.all, 'prompts', id],
};

export const useQuestions = (languageId) => useQuery({
    queryKey: QUERY_KEYS.list({ languageId }),
    queryFn: () => aiReviewApi.getQuestions(languageId),
    enabled: !!languageId,
});

export const useQuestionDetail = (questionId) => useQuery({
    queryKey: QUERY_KEYS.detail(questionId),
    queryFn: async () => {
        const [detail, prompts] = await Promise.all([
            aiReviewApi.getQuestionDetail(questionId),
            aiReviewApi.getPrompts(questionId),
        ]);
        const detailData = detail?.data ?? detail;
        const promptsData = prompts?.data ?? prompts;
        return { ...detailData, prompts: promptsData };
    },
    enabled: !!questionId,
});

export const useCreateQuestion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: aiReviewApi.createQuestion,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
        },
    });
};

export const useUpdateQuestion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: aiReviewApi.updateQuestion,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(variables.questionId) });
        },
    });
};

export const useDeleteQuestion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: aiReviewApi.deleteQuestion,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
        },
    });
};

export const useApproveQuestion = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: aiReviewApi.approveQuestion,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.detail(variables.questionId) });
        },
    });
};